import React, { useEffect, useState } from 'react';
import { Html5Table } from 'window-table';
import { IoTriangle, IoTriangleSharp } from 'react-icons/io5';
import { Button } from '@/components/ui/button';
import { toLocaleShortString, toRate, toSpecialFixed } from '@/lib/misc/utils';
import { getNetworkLogo, getTokenImageFromAssetItem } from '@/lib/misc/tokenImageHelper';
import CustomDialog from '@/components/ui/customDialog';
import DTFlexYourGains from './DTFlexYourGains';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/contexts/LitSessionProvider';
import TokenToSend from './TokenToSend';
import { useMixpanel } from '@/lib/misc/mixpanel';
import classNames from '@/lib/misc/classNames';
import DTPortfolioAssetsRow from './DTPortfolioAssetsRow';
import TokenImage from '@/components/shared/TokenImage';
interface PortfolioAssetListProps {
  isSwap?: boolean;
  balances: any[];
  setLinkClickedLoading?: (value: boolean) => void;
}
export const DTPortfolioAssetsTable = ({
  balances,
  isSwap,
  setLinkClickedLoading
}: PortfolioAssetListProps) => {
  const [sortedBalances, setSortedBalances] = useState([]);
  const {
    PKP
  } = useSession();
  const {
    LogToMixpanel,
    EVENTS,
    CATEGORIES
  } = useMixpanel();
  const tableRef = React.useRef<HTMLDivElement>(null);
  const [tableHeight, setTableHeight] = useState(0);
  const [sendModalData, setSendModalData] = useState<{
    address: string;
    network: number;
  } | null>(null);
  const [flexModalData, setFlexModalData] = useState<any>(null);
  useEffect(() => {
    const calculateHeight = () => {
      if (tableRef.current) {
        const tableTop = tableRef.current.getBoundingClientRect().top;
        const windowHeight = window.innerHeight;
        setTableHeight(windowHeight - tableTop);
      }
    };

    // Calculate initial height
    calculateHeight();

    // Recalculate on window resize
    window.addEventListener('resize', calculateHeight);
    return () => {
      window.removeEventListener('resize', calculateHeight);
    };
  }, []);
  function handleClick(row) {
    if (setLinkClickedLoading) {
      setLinkClickedLoading(true);
    }
    LogToMixpanel({
      pkp: PKP,
      eventName: EVENTS.portfolio.clickToken,
      category: CATEGORIES.portfolio,
      metaData: {
        tokenAddress: row.contract_address,
        networkId: row.network
      }
    });
  }
  useEffect(() => {
    const filtered = balances?.filter((x: any) => x?.contract_address).filter(x => x.usd_value > 0).sort((a: any, b: any) => b.usd_value - a.usd_value);
    setSortedBalances(filtered || []);
  }, [balances]);
  const columns = [{
    key: 'ticker',
    width: 150,
    title: 'Ticker',
    HeaderCell: props => <tr {...props} className='py-2 px-5' />,
    Component: ({
      row
    }) => {
      const loading = !row?.contract_name;
      const tokenImage = getTokenImageFromAssetItem(row);
      return <div className='w-full flex items-center min-h-[70px] pl-4'>
            <div className={classNames(`${!isSwap ? 'w-5/12' : 'w-3/4'} cursor-pointer`)}>
              <div className='flex items-center'>
                {loading ? <div className='flex items-center gap-3'>
                    <div className='relative'>
                      <Skeleton className='rounded-full h-[28px] w-[28px]' />
                      <Skeleton className='absolute rounded-full h-[14px] w-[14px] -bottom-1 -right-1' />
                    </div>

                    <div className='flex flex-col items-start'>
                      <Skeleton className='w-16 h-4' />
                      <Skeleton className='w-12 h-3 mt-1' />
                    </div>
                  </div> : <>
                    {/* <AutoTokenImage
                     tokenAddress={row?.contract_address}
                     network={row?.network}
                     size={28}
                     badgeSize={14}
                     /> */}
                    <TokenImage mainImageSrc={tokenImage} badgeImageSrc={getNetworkLogo(row?.network)} size={28} badgeSize={14} />
                    <div className='ml-3 flex flex-col items-start whitespace-nowrap'>
                      <p className='text-[14px]'>{toLocaleShortString(row?.contract_name, 12)}</p>
                      <p className='text-[12px] text-genius-cream/50'>
                        {toLocaleShortString(row?.contract_ticker_symbol)}
                      </p>
                    </div>
                  </>}
              </div>
            </div>
          </div>;
    }
  }, {
    key: 'balance',
    width: 100,
    title: 'Balance',
    HeaderCell: props => <tr {...props} className='py-2 ta-r' />,
    Component: ({
      row
    }) => {
      const loading = !row?.contract_name;
      return <div className='ta-r flex flex-col justify-center h-full'>
            {loading ? <div className='flex flex-col items-end'>
                <Skeleton className='w-16 h-4' />
                <Skeleton className='w-16 h-3 mt-1' />
              </div> : <>
                <p className='text-[16px]'>{`$${toSpecialFixed(row.usd_value, 2)?.value}`}</p>
                <p className='text-xs text-genius-cream/50'>
                  {`${toSpecialFixed(row.formatted_balance, 2)?.value} ${toLocaleShortString(row.contract_ticker_symbol, 3, true)}`}
                </p>
              </>}
          </div>;
    }
  }, {
    key: 'price',
    width: 100,
    title: 'Price',
    HeaderCell: props => <tr {...props} className='py-2 ta-r' />,
    Component: ({
      row
    }) => {
      const loading = !row?.contract_name;
      const rateChange = toRate(row.quote_rate, row.quote_rate_24h);
      return <div className='ta-r flex flex-col justify-center h-full'>
            {loading ? <div className='flex flex-col items-end'>
                <Skeleton className='w-16 h-4' />
                <Skeleton className='w-16 h-3 mt-1' />
              </div> : <>
                <p className='text-[16px]'>{`$${toSpecialFixed(row.quote_rate, 2)?.value}`}</p>
                <div className='flex items-center justify-end text-[12px]'>
                  {rateChange > 0 ? <>
                      <IoTriangle color='#34D399' size={12} />
                      <p className='text-green-400 ml-1'>
                        {`${toSpecialFixed(rateChange, 2)?.value}`}%
                      </p>
                    </> : <>
                      <IoTriangle color='#FF5B5B' className='rotate-180' size={12} />
                      <p className='text-red-400 ml-1'>
                        {`${toSpecialFixed(Math.abs(rateChange), 2)?.value}`}%
                      </p>
                    </>}
                </div>
              </>}
          </div>;
    }
  }, {
    key: 'returns',
    width: 100,
    title: 'Returns',
    HeaderCell: props => <tr {...props} className='py-2 ta-r' />,
    Component: ({
      row
    }) => {
      const loading = !row?.contract_name;
      const currentGains = row.tokenPnLData?.realisedPNL + row.tokenPnLData?.unrealisedPNL;
      return <div className='ta-r flex flex-col justify-center h-full'>
            {loading ? <div className='flex flex-col items-end'>
                <Skeleton className='w-16 h-4' />
                <Skeleton className='w-16 h-3 mt-1' />
              </div> : isNaN(parseFloat(currentGains)) ? <div className='flex flex-col items-end justify-end'>
                <Skeleton className='w-20 h-4' />
                <Skeleton className='w-14 h-4 mt-1' />
              </div> : <>
                <p className='text-[16px]'>
                  {Number(currentGains) > 0 && <span>+</span>}
                  {`$${toSpecialFixed(parseFloat(currentGains), 2).value}`.replace('$-', '- $')}
                </p>
                <div className='flex justify-end items-center ta-r text-[12px]'>
                  {row.tokenPnLData?.actualPnLChangePercent >= 0 ? <>
                      <IoTriangleSharp color='#34D399' size={12} />
                      <p className='text-green-400 ml-1'>
                        {toSpecialFixed(row.tokenPnLData?.actualPnLChangePercent, 2).value}%
                      </p>
                    </> : <>
                      <IoTriangleSharp color='#FF5B5B' className='rotate-180' size={12} />
                      <p className='text-red-400 ml-1'>
                        {toSpecialFixed(row.tokenPnLData?.actualPnLChangePercent, 2).value.replace('-', '')}
                        %
                      </p>
                    </>}
                </div>
              </>}
          </div>;
    }
  }, {
    key: 'actions',
    width: 100,
    title: '',
    Component: ({
      row
    }) => {
      const loading = !row?.contract_name;
      return <div className='flex justify-end items-center h-full mr-4 '>
            {loading ? <Skeleton className='w-20 h-6' /> : <Button className={`flexButton bg-genius-blue float-right hover:bg-genius-pink hover:text-genius-blue rounded-full w-[91px] h-[24px] flex items-center`} variant='ghost' onClick={() => setFlexModalData(row)}>
                Flex
                <p className='ml-2 flexButton'>🎉</p>
              </Button>}
          </div>;
    }
  }];
  return <div className='flex-1 w-full no-scrollbar' data-sentry-component="DTPortfolioAssetsTable" data-sentry-source-file="DTPortfolioAssetsTable.tsx">
      <div ref={tableRef}>
        <Html5Table height={tableHeight} data={sortedBalances} columns={columns} rowHeight={70} className='no-scrollbar' style={{
        overflow: 'hidden'
      }} HeaderRow={props => <tr {...props} className='bg-genius-blue text-[14px]' />} disableHeader={false} Row={props => <DTPortfolioAssetsRow props={props} PKP={PKP} handleClick={handleClick} setSendModalData={setSendModalData} />} data-sentry-element="Html5Table" data-sentry-source-file="DTPortfolioAssetsTable.tsx" />
      </div>

      <CustomDialog isOpen={sendModalData !== null} toggleModal={() => setSendModalData(null)} className={'p-0 h-[522px] w-[572px]'} data-sentry-element="CustomDialog" data-sentry-source-file="DTPortfolioAssetsTable.tsx">
        <TokenToSend address={sendModalData?.address} network={sendModalData?.network} data-sentry-element="TokenToSend" data-sentry-source-file="DTPortfolioAssetsTable.tsx" />
      </CustomDialog>

      <CustomDialog hideClose={true} className='p-0 bg-transparent border-none shadow-none' isOpen={flexModalData !== null} toggleModal={() => setFlexModalData(null)} data-sentry-element="CustomDialog" data-sentry-source-file="DTPortfolioAssetsTable.tsx">
        <DTFlexYourGains tokenData={flexModalData} tokenPnLData={flexModalData?.tokenPnLData} isLoading={flexModalData?.isLoading} data-sentry-element="DTFlexYourGains" data-sentry-source-file="DTPortfolioAssetsTable.tsx" />
      </CustomDialog>
    </div>;
};
export default DTPortfolioAssetsTable;